import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from "react";

import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import useDecodedParams from "Hooks/useDecodedParams";
import useEnvironments from "Hooks/useEnvironments";
import { useLocalForage } from "Hooks/useLocalForage";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { sortBy } from "Libs/utils";
import {
  environmentsAsArraySelector,
  environmentTreeSelector,
  environmentSelector
} from "Reducers/environment";
import { useAppSelector } from "Store/hooks";

import ResourcesSticker from "../EnvironmentTree/ResourcesSticker";

import * as S from "./EnvironmentTree.styles";
import { loadSettings, persistSettings } from "./persist-settings";
import { EnvironmentTreeData, getAllEnvironments, getTreeData } from "./utils";

import { EnvironmentsTreeSkeleton, List, NavBar, Nodes } from "./";

const RESIZE_LIST_WIDTH = 516;

type Props = {
  showResourcesSticker: boolean;
};
const EnvironmentTree = ({ showResourcesSticker }: Props) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const scrollEvent = new Event("scrolled");
  const location = useLocation();

  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const {
    value: listLayout = false,
    setValue: setListLayout,
    isLoading: isLoadingLayout
  } = useLocalForage<boolean>("projects_environment-list_layout");

  const [sort, setSort] = useState<{
    type: "title" | "type" | "created_at" | "deployed_at";
    order: "asc" | "desc";
  }>({
    type: "title",
    order: "asc"
  });

  const [showEnvs, setShowEnvs] = useState({ active: true, inactive: true });
  const [titleFilter, setTitleFilter] = useState("");
  const targetRef = useRef<HTMLDivElement>(null);

  const [isBigScreen, setIsBigScreen] = useState(false);

  const checkIsBigScreen = (e: HTMLDivElement | null) => {
    if (!e) return false;
    return e.offsetWidth > RESIZE_LIST_WIDTH;
  };

  const handleWindowResize = useCallback(() => {
    if (checkIsBigScreen(targetRef.current)) {
      setIsBigScreen(true);
    } else {
      setIsBigScreen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [handleWindowResize]);

  useEffect(() => {
    handleWindowResize();
  }, [handleWindowResize]);

  const envTree = useSelectorWithUrlParams(environmentTreeSelector);
  const envList = useSelectorWithUrlParams(environmentsAsArraySelector);

  useEffect(() => {
    let isCanceled = false;
    const setDefaultSettings = async () => {
      const settings = await loadSettings(projectId);
      if (isCanceled) return;

      if (settings.sort) setSort(settings.sort);
      if (settings.showEnvs) setShowEnvs(settings.showEnvs);
    };

    setDefaultSettings();

    return () => {
      isCanceled = true;
    };
  }, [projectId]);

  useEffect(() => {
    const filters = {
      showEnvs,
      sort
    };
    Object.keys(filters).forEach(key => {
      if (filters[key as keyof typeof filters] === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete filters[key as keyof typeof filters];
      }
    });
    persistSettings(projectId, filters);
  }, [projectId, showEnvs, sort]);

  const [treeData = [], environments = []] = useMemo(() => {
    if (!envList) return [];
    const rootEnv = envList.filter(env => !env.parent);
    const data = getTreeData(rootEnv, envList);
    return [data, getAllEnvironments(data)];
  }, [envList]);

  const filteredEnvs = useMemo(() => {
    if (!listLayout) return [];
    let list = sortBy(environments, sort.type)
      .filter(
        env =>
          (showEnvs.active && env.status !== "inactive") ||
          (showEnvs.inactive && env.status === "inactive")
      )
      .filter(env =>
        titleFilter
          ? env.title.toLowerCase().includes(titleFilter.toLowerCase())
          : true
      );
    if (sort.order === "desc") list = list.reverse();
    return list;
  }, [environments, listLayout, showEnvs, sort, titleFilter]);

  const visibleNodes = useMemo(() => {
    if (listLayout) return;

    let list = envTree || [];
    if (titleFilter) {
      list = list.filter(env =>
        env.id.toLowerCase().includes(titleFilter.toLowerCase())
      );
    }
    return list.reduce<string[]>((acc, cu) => {
      acc = [...new Set(acc.concat(cu.path || [], cu.id))];
      return acc;
    }, []);
  }, [envTree, listLayout, titleFilter]);

  const countEnv = (arr: EnvironmentTreeData[]) =>
    arr.reduce((acc, { children = [] }) => {
      acc++;
      if (children.length) acc += countEnv(children);
      return acc;
    }, 0);

  const listEnvironments = listLayout ? filteredEnvs : treeData;
  const allEnvironments = useEnvironments(projectId);
  const envId =
    environmentId ||
    Object.values(allEnvironments ?? {}).find(env => env?.type === "production")
      ?.id;

  const environment = useAppSelector(state =>
    environmentSelector(state, {
      environmentId: envId,
      organizationId,
      projectId
    })
  );

  const [resourcesUrl] = useUrls(
    "organization.project.environment.configureResources",
    {
      environmentId: environment?.name || "",
      location: location.pathname + location.search,
      queryParams: ""
    }
  );

  const toggleSort = (
    e: React.MouseEvent | undefined,
    type: "title" | "type" | "created_at" | "deployed_at"
  ) => {
    if (e?.preventDefault) e.preventDefault();

    setSort({
      type,
      order: sort.type === type && sort.order === "asc" ? "desc" : "asc"
    });
  };

  return (
    <S.Wrapper aria-labelledby="environments-heading">
      {showResourcesSticker && (
        <ResourcesSticker
          environmentTitle={environment?.title!}
          url={resourcesUrl}
        />
      )}
      <S.Header>
        <S.Title tag="h2">{intl.formatMessage({ id: "environments" })}</S.Title>
        {!isLoadingLayout && (
          <NavBar
            setShowEnvs={setShowEnvs}
            setTitleFilter={setTitleFilter}
            setTreeLayout={setListLayout}
            showEnvs={showEnvs}
            sortType={sort.type}
            titleFilter={titleFilter}
            toggleSort={toggleSort}
            treeLayout={!listLayout}
          />
        )}
      </S.Header>

      {!envList ? (
        <EnvironmentsTreeSkeleton />
      ) : (
        <S.Layout ref={ref}>
          {listLayout ? (
            <List
              environments={listEnvironments}
              sort={sort}
              titleFilter={titleFilter}
              toggleSort={toggleSort}
            />
          ) : (
            <S.Scrollbox
              {...(countEnv(listEnvironments) > 14 && {
                style: { height: "735px" }
              })}
              onScroll={() => document.dispatchEvent(scrollEvent)}
            >
              <S.TreeWrapper ref={targetRef}>
                <Nodes
                  nodes={listEnvironments}
                  organizationId={organizationId}
                  showEnvs={showEnvs}
                  titleFilter={titleFilter}
                  visibleNodes={visibleNodes}
                  isBigScreen={isBigScreen}
                />
              </S.TreeWrapper>
            </S.Scrollbox>
          )}
        </S.Layout>
      )}
    </S.Wrapper>
  );
};

export default EnvironmentTree;
